import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFiltersPayload } from 'pages/admin/demand/planning/components/tabs/planningByLevel/utils/planningByLevelUtils';
import api from 'services/api/api';

import { PlanningTypes } from '../../planning/context/types/contextPrevision.type';

interface IDefaultSetupParams {
	planningType: PlanningTypes;
	cycleId: number | string;
	companyAreaId?: number | string;
}

export interface IDefaultSetupResponse {
	base_type: string;
	based_cycle_id?: string | number;
	sales_based_apportionment_setup?: {
		sale_start_date: string;
		sale_end_date: string;
	};
	based_cycle: {
		month_year: string;
	};
}
export interface ISaveSetupParams {
	selectedBase: string;
	startMonth?: string;
	endMonth?: string;
	selectedCycleId?: number | string;
	selectedCycleLabel?: string;
	selectedCompanyAreaId?: number | string;
	saveAsDefault: boolean;
}
const formatDate = (dateStr?: string) => {
	if (!dateStr) return dateStr;
	const [year, month] = dateStr.split('-');
	return `${year}-${month}-01`;
};
export const buildApportionmentSetup = (setup: ISaveSetupParams) => {
	const {
		saveAsDefault,
		selectedBase,
		endMonth,
		selectedCycleId,
		selectedCompanyAreaId,
		startMonth,
	} = setup;
	return {
		cycle_id: Number(selectedCycleId),
		company_area_id: Number(selectedCompanyAreaId),
		based: selectedBase,
		default: saveAsDefault,
		...(selectedBase === 'sales'
			? {
					sale_start_date: formatDate(startMonth),
					sale_end_date: formatDate(endMonth),
			  }
			: {
					selected_cycle: selectedCycleId,
			  }),
		based_company_area_id: selectedCompanyAreaId,
	};
};

export const useGetDefaultSetup = (params: IDefaultSetupParams) => {
	return useQuery<IDefaultSetupResponse>({
		queryKey: [
			'defaultSetup',
			params.planningType,
			params.cycleId,
			params.companyAreaId || '',
		],
		queryFn: async () => {
			const response = await api.get(`/apportionment/setup/default`, {
				params: buildFiltersPayload({
					cycleId: params.cycleId,
					planningType: params.planningType,
					companyAreaId: params.companyAreaId,
				}),
			});
			return response.data;
		},
		enabled: !!params.cycleId,
		refetchOnWindowFocus: false,
	});
};
export const saveDefaultSetup = async (
	params: IDefaultSetupParams,
	setup: ISaveSetupParams
): Promise<undefined> => {
	const url = `/apportionment/setup/default`;
	const response = await api.post(url, buildApportionmentSetup(setup), {
		params: buildFiltersPayload({
			cycleId: params.cycleId,
			planningType: params.planningType,
			companyAreaId: params.companyAreaId,
		}),
	});

	return response.data;
};

export const useSaveDefaultSetup = (params: IDefaultSetupParams) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (setup: ISaveSetupParams) =>
			saveDefaultSetup(params, setup),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					'defaultSetup',
					params.planningType,
					params.cycleId,
					params.companyAreaId || '',
				],
			});
		},
	});
};
